import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Building2, Check, Loader2, Plus, Save, Users } from "lucide-react";

import { EmptyState, ErrorBanner, PageHeader } from "@/components/common/FeatureShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCurrentOrg } from "@/contexts/OrgContext";
import { useOrgCreateMutation, useOrgUpdateMutation } from "@/hooks/queries/useOrgs";
import { cn } from "@/lib/utils";

function slugify(value: string) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
}

function OrgProfileSection() {
  const { currentOrg } = useCurrentOrg();
  const updateMutation = useOrgUpdateMutation(currentOrg?.id ?? "");
  const [name, setName] = useState(currentOrg?.name ?? "");
  const [slug, setSlug] = useState(currentOrg?.slug ?? "");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setName(currentOrg?.name ?? "");
    setSlug(currentOrg?.slug ?? "");
  }, [currentOrg?.id, currentOrg?.name, currentOrg?.slug]);

  if (!currentOrg) {
    return null;
  }

  const canManage = currentOrg.role === "owner" || currentOrg.role === "admin";

  const save = async () => {
    setError(null);
    try {
      await updateMutation.mutateAsync({ name: name.trim(), slug: slug.trim() });
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Organization could not be saved.");
    }
  };

  return (
    <section className="rounded-md border bg-card p-4">
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-sm font-semibold"><Building2 className="size-4" aria-hidden />{currentOrg.name}</h2>
        <Button type="button" variant="outline" size="sm" asChild>
          <Link to="/settings/organization/members"><Users className="size-4" />Members ({currentOrg.member_count})</Link>
        </Button>
      </div>
      <ErrorBanner className="mt-3" message={error} />
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <div className="space-y-2"><Label htmlFor="org-name">Name</Label><Input id="org-name" value={name} onChange={(e) => setName(e.target.value)} disabled={!canManage} /></div>
        <div className="space-y-2"><Label htmlFor="org-slug">Slug</Label><Input id="org-slug" value={slug} onChange={(e) => setSlug(slugify(e.target.value))} disabled={!canManage} /></div>
        <div className="space-y-2"><Label htmlFor="org-plan">Plan</Label><Input id="org-plan" value={currentOrg.plan} readOnly /></div>
        <div className="space-y-2"><Label htmlFor="org-status">Status</Label><Input id="org-status" value={currentOrg.status} readOnly /></div>
      </div>
      {canManage ? (
        <Button type="button" className="mt-4" onClick={() => void save()} disabled={updateMutation.isPending}>
          {updateMutation.isPending ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}Save organization
        </Button>
      ) : (
        <p className="mt-4 text-xs text-muted-foreground">Only owners and admins can edit organization settings.</p>
      )}
    </section>
  );
}

function CreateOrgSection() {
  const { setCurrentOrg } = useCurrentOrg();
  const createMutation = useOrgCreateMutation();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = async () => {
    setError(null);
    try {
      const org = await createMutation.mutateAsync({ name: name.trim(), slug: slug.trim() });
      setCurrentOrg(org.id);
      setName("");
      setSlug("");
      setSlugEdited(false);
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : "Organization could not be created.");
    }
  };

  return (
    <section className="rounded-md border bg-card p-4">
      <h2 className="text-sm font-semibold">Create organization</h2>
      <ErrorBanner className="mt-3" message={error} />
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="new-org-name">Name</Label>
          <Input id="new-org-name" value={name} onChange={(e) => { setName(e.target.value); if (!slugEdited) setSlug(slugify(e.target.value)); }} placeholder="Acme Legal" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="new-org-slug">Slug</Label>
          <Input id="new-org-slug" value={slug} onChange={(e) => { setSlug(slugify(e.target.value)); setSlugEdited(true); }} placeholder="acme-legal" />
        </div>
      </div>
      <Button type="button" className="mt-4" onClick={() => void create()} disabled={createMutation.isPending || !name.trim() || slug.length < 3}>
        {createMutation.isPending ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}Create organization
      </Button>
    </section>
  );
}

/**
 * Screen composition — Settings (Organization)
 *
 * Hero zone: org profile and workspace cards (`max-w-3xl`, `space-y-6`).
 * Scanning regions (compact): workspace list rows, member count link.
 * Focus regions (airy): form fields, card sections with internal padding.
 */
export default function OrganizationSettingsPage() {
  const { orgs, orgsLoading, currentOrgId, setCurrentOrg } = useCurrentOrg();

  return (
    <div className="min-h-screen">
      <PageHeader title="Organization" description="Manage your organizations, members, and workspace settings." />
      <main className="max-w-3xl space-y-6 p-4 md:p-6">
        <section className="rounded-md border bg-card p-4">
          <h2 className="text-sm font-semibold">Your workspaces</h2>
          <div className="mt-3 space-y-1">
            {orgsLoading ? <p className="px-3 py-2 text-sm text-muted-foreground">Loading organizations…</p> : null}
            {orgs.map((org) => (
              <button
                key={org.id}
                type="button"
                onClick={() => setCurrentOrg(org.id)}
                className={cn("flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm hover:bg-accent", currentOrgId === org.id && "bg-secondary")}
              >
                <Building2 className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                <span className="flex-1 truncate">{org.name}</span>
                <span className="text-xs text-muted-foreground">{org.role}</span>
                {currentOrgId === org.id ? <Check className="size-4" aria-hidden /> : null}
              </button>
            ))}
            {!orgsLoading && orgs.length === 0 ? (
              <EmptyState title="No organizations yet" description="Create one below to start collaborating with your firm." />
            ) : null}
          </div>
        </section>
        <OrgProfileSection />
        <CreateOrgSection />
      </main>
    </div>
  );
}
